/**
 * **The sloop's tick**: where she is, what her mountings are pointing at, and
 * the rounds her two Maxims put up.
 *
 * She is deliberately assembled out of machinery that already existed, and the
 * list of what is *new* here is short on purpose:
 *
 * | what she does | whose machine does it |
 * |---|---|
 * | steams a patrol | `ship-patrol.ts` — closed form in the clock, no state |
 * | is shot at, and hashed | `targets.ts` — she is an `EncounterTarget`, like a searchlight post |
 * | throws 3-pounder bursts | `flak.ts` — she carries a real `FlakBattery` that moves with her |
 * | fires two Maxims | `defensive-fire.ts` — the Zeppelin's own gunner machine |
 * | is drawn | nothing new: her bursts are `FlakBurst`s and her rounds are `RoundRecord`s |
 * | is heard | her bursts, free, through archie's own path; her Maxims took a signal of their own — `apps/web/src/flight/frame-signals.ts`'s `firingBatteryRangesM`, and `ship-sim.md` §4.2b for why the row above was not enough |
 *
 * What this file adds is the wiring and one honest new thing: the **mount aims**
 * the renderer swings the rig by.
 *
 * ## Her flak is a battery that moves, and that is the only thing `flak.ts` had
 * to be told
 *
 * `FlakBattery` is a plain record with a `position`, and `stepFlak` reads that
 * position afresh every tick — nothing in it assumes a battery is bolted down.
 * So a ship's 3-pounders are a battery whose emplacement is rewritten from her
 * pose at the top of each tick, and every consequence follows for free: the
 * bursts are `FlakBurst`s the renderer and the audio already draw and play, the
 * damage is `RoundHit`s with `shooterId` set to the battery, and the
 * attribution lands exactly where ground flak's does —
 * `encounter-settle.ts` keeps archie last in the precedence list and
 * `mission-run.ts`'s `killerOf` resolves a battery id to no name at all, so
 * being shot down by a ship gives the original's own unnamed `VANQUISHED!`
 * page, as being shot down by a field battery does.
 *
 * Two departures from a field battery, both deliberate and both stated at their
 * constants in `ship-class.ts`: she has **two** guns rather than five
 * (`SHIP_FLAK_GUNS`), and they are the **light** class rather than the heavy one
 * (`SHIP_FLAK_CLASS`), which is 762 m of horizontal reach rather than 1905.
 *
 * ## The main gun trains and does not fire
 *
 * `SHIP_MAIN_GUN_FIRES` is `false`, and `mountAims` still reports a bearing for
 * the 4.7 in whenever anything hostile is inside its ±150° arc. That costs one
 * `stationAim` call and no draws, and it is the difference between a warship and
 * a warship with a jammed turret.
 *
 * ## The stream, and the one thing this lane traded away
 *
 * Her Maxims draw on **`airship`** — `AIRSHIP_RNG_STREAM` — rather than on a
 * stream of their own. That is a trade and it is worth naming: `RNG_STREAM_NAMES`
 * is one of `engine-fingerprint.ts`'s four inputs, so a tenth stream would move
 * the fingerprint of every build, refuse every recorded tape and re-cut every
 * pinned census — while the whole point of ENHANCED SIMULATION is that it is
 * **bit-zero with the switch off**. The stream is in any case the right shape
 * for its tenant: it is the *defensive-gunner* stream, named for the first
 * thing that had one. The cost is that a Zeppelin and a ship in one encounter
 * would share a stream, which no mission this port generates can produce; the
 * day one does is the day a `shipGuns` stream and a fingerprint bump are owed,
 * and this paragraph is the notice.
 *
 * Her 3-pounders draw on `flak`, because `stepFlak` names that stream itself.
 */

import type { RngBank } from "../flight/rng.ts";
import type { Tunables } from "../flight/tunables.ts";
import { addVec3, attitudeFromAngles, rotateVec3, type Vec3 } from "../flight/math3d.ts";
import { AIRSHIP_RNG_STREAM } from "./airship-pass.ts";
import {
  defensiveFire,
  stationAim,
  type DefensiveBelt,
  type DefensiveGunSpec,
  type DefensiveQuarry,
} from "./defensive-fire.ts";
import type { CombatBody } from "./geometry.ts";
import { createFlakBattery, type FlakBattery, type FlakBatterySpec } from "./flak-battery.ts";
import type { RoundRecord } from "./rounds.ts";
import {
  SHIP_BATTERY_OFFSET_M,
  SHIP_FLAK_CLASS,
  SHIP_FLAK_GUNS,
  SHIP_FLAK_MOUNT_INDICES,
  SHIP_MAIN_MOUNT_INDEX,
  SHIP_MG_ENGAGEMENT_RANGE_M,
  SHIP_MG_MOUNT_INDICES,
  SHIP_MG_RANGE_CAP_M,
  SHIP_MOUNTS,
  SHIP_SPEED_MPS,
  shipMachineGunSpec,
  shipStationBelts,
} from "./ship-class.ts";
import { shipFightsOn } from "./ship-damage.ts";
import type { ShipSpec } from "./ship-deployment.ts";
import { patrolPoseAt } from "./ship-patrol.ts";
import type { EncounterTarget, EncounterTargetSpec } from "./targets.ts";
import { targetBody } from "./targets.ts";
import type { Traverse } from "./traverse.ts";

/** The kind a ship's hull is created as. Named once, so nobody spells it twice. */
export const SHIP_KIND = "ship" as const;

/**
 * The graze scatter on a ship's Maxim, in BAM16 counts.
 *
 * `MG.DAT`'s own `±(rand() & 0x7f)`, borrowed with the rest of the machine-gun
 * numbers (`ship-class.ts`'s module note). It is the only recovered figure this
 * port has for how badly a defensive gunner misses per round, and a Maxim on a
 * pintle does not become a different weapon for being at sea.
 */
export const SHIP_GRAZE_SCATTER_BAM = 0x7f;

/**
 * How well a ship's gunners aim, as a multiple of `aiAimErrorRad` — the same
 * scale `airship-pass.ts` argues for, and the same value.
 *
 * The two arguments that pushed a Zeppelin's gunner to "an ordinary pilot" pull
 * the same way here and one of them harder: he has a stable platform and no
 * aeroplane to fly, which argues up, and he is shooting at a scout crossing his
 * arc at 45 m/s, which argues down. A ship rolls where a gasbag does not, so if
 * anything this is generous. CALIBRATION-PENDING, like every other unrecovered
 * gunnery constant in this port.
 */
export const SHIP_GUNNER_AIM_ERROR_SCALE = 2.1;

/** Rounds a ship fires between redraws of her gunners' standing aim error. `airship-pass.ts` §5's own. */
export const SHIP_AIM_EPOCH_ROUNDS = 72;

/** Ticks of phase between her two Maxims' cadences, so the pair reads as two gunners rather than one. */
const STATION_PHASE_TICKS = 7;

/**
 * How far the 4.7 in will train toward a threat, metres.
 *
 * Generous — it is a training bearing and not a firing solution, and a big gun
 * following an aeroplane it is never going to shoot at is the picture. Beyond
 * this the gunhouse rests where it is.
 */
const MAIN_TRAINING_RANGE_M = 4_000;

/**
 * How far a **Maxim** mounting will follow a target, metres — the ship's own
 * `SHIP_MG_RANGE_CAP_M`, which is **228.6 m (1.33×) beyond the range she opens
 * fire at**.
 *
 * A gunner is on his aeroplane before he shoots at it, and a barrel that snaps
 * onto its target at the same instant the first round leaves it reads as a
 * machine rather than as a man. This is that margin, and it is not a fresh
 * invention: it is the user's declared 3,000 ft used as the *outer* edge of
 * everything a Maxim does, with `MG.DAT`'s tighter 685.8 m deciding where the
 * belt is actually spent.
 *
 * The margin closes rather than opens if the two numbers ever meet —
 * `SHIP_MG_ENGAGEMENT_RANGE_M` is itself capped at this value — so no mounting
 * can ever be seen tracking, and no round or tracer produced, past the cap.
 *
 * Costs no draws: `stationAim` reaches `stationTarget`, which is pure, and the
 * whole `ShipAim` list is a renderer output that is never hashed and never
 * carried between ticks.
 */
const MG_TRACKING_RANGE_M = SHIP_MG_RANGE_CAP_M;

/** Her hull's id is the ship's; her battery's is derived from it, the way a searchlight post's is. */
export function shipBatteryId(shipId: string): string {
  return `${shipId}~guns`;
}

const BATTERY_ID_PATTERN = /^(.+)~guns$/;

/** The ship a battery belongs to, or `null` for a battery that is not a ship's. */
export function shipOfBatteryId(batteryId: string): string | null {
  return BATTERY_ID_PATTERN.exec(batteryId)?.[1] ?? null;
}

/** Where she is and which way she is heading at `timeS` seconds into the encounter. */
export function shipPoseAt(ship: ShipSpec, timeS: number): CombatBody {
  const point = patrolPoseAt(ship.patrol, timeS, SHIP_SPEED_MPS);
  const orientation = attitudeFromAngles(point.headingRad, 0, 0);
  // Her velocity is her heading times her speed, which is exactly what a lead
  // solver needs and what makes an aeroplane's gunners have to lead her.
  return {
    position: { x: point.x, y: 0, z: point.z },
    velocity: {
      x: Math.sin(point.headingRad) * SHIP_SPEED_MPS,
      y: 0,
      z: -Math.cos(point.headingRad) * SHIP_SPEED_MPS,
    },
    orientation,
  };
}

/**
 * A point on the ship, in world metres, from an offset in her own body frame —
 * the same `position + rotate(orientation, offset)` `geometry.ts`'s
 * `muzzlePosition` applies to an aeroplane, and deliberately through the
 * quaternion rather than through a heading recovered from her velocity: a
 * heading read back out of a velocity is a second source of truth, and the
 * first version of this function had it a half-turn out.
 */
function onShip(body: CombatBody, offsetM: Vec3): Vec3 {
  return addVec3(body.position, rotateVec3(body.orientation, offsetM));
}

/** Her hull, as the `EncounterTargetSpec` an encounter is opened with. */
export function shipTargetSpec(ship: ShipSpec): EncounterTargetSpec {
  const point = patrolPoseAt(ship.patrol, 0, SHIP_SPEED_MPS);
  return {
    id: ship.id,
    kind: SHIP_KIND,
    side: ship.side,
    position: { x: point.x, y: 0, z: point.z },
    headingRad: point.headingRad,
    label: "Escort sloop",
  };
}

/**
 * Where her 3-pounders stand at `timeS` — the emplacement `flak.ts` lays,
 * fires and measures range from, rewritten every tick by `encounter-tick.ts`'s
 * pass 0.
 *
 * `flak-battery.ts`'s `fuseSecondsOf` reads the emplacement's own `y` as its
 * altitude, so keeping the mounting's 5.76 m above the waterline here is not
 * cosmetic: it is four metres of fuse setting the same guns ashore would have.
 */
export function shipBatteryPosition(ship: ShipSpec, timeS: number): Vec3 {
  return onShip(shipPoseAt(ship, timeS), SHIP_BATTERY_OFFSET_M);
}

/** Her 3-pounders, as the emplacement `flak.ts` opens a battery from. */
export function shipBatterySpec(ship: ShipSpec): FlakBatterySpec {
  return {
    id: shipBatteryId(ship.id),
    side: ship.side,
    position: shipBatteryPosition(ship, 0),
    batteryClass: SHIP_FLAK_CLASS,
  };
}

/** Her battery, with `SHIP_FLAK_GUNS` slots rather than a field battery's five. */
export function createShipBattery(ship: ShipSpec): FlakBattery {
  const full = createFlakBattery(shipBatterySpec(ship));
  return { ...full, slots: full.slots.slice(0, SHIP_FLAK_GUNS) };
}

/** The belts her Maxims spawn with — what `createTarget` puts in `gunRoundsRemaining`. */
export function shipBelts(): readonly number[] {
  return shipStationBelts();
}

/** Her Maxims as the `DefensiveGunSpec` the shared gunner machine spends. */
function shipGunnery(tunables: Tunables): DefensiveGunSpec {
  return {
    gun: shipMachineGunSpec(tunables.gunRoundsPerSecond),
    engagementRangeM: SHIP_MG_ENGAGEMENT_RANGE_M,
    grazeScatterBam: SHIP_GRAZE_SCATTER_BAM,
    aimErrorScale: SHIP_GUNNER_AIM_ERROR_SCALE,
    aimEpochRounds: SHIP_AIM_EPOCH_ROUNDS,
    stationPhaseTicks: STATION_PHASE_TICKS,
    stream: AIRSHIP_RNG_STREAM,
  };
}

/** Where every mounting on one ship is pointing this tick, in `SHIP_MOUNTS` order. `null` means "at rest". */
export interface ShipAim {
  readonly shipId: string;
  readonly mounts: readonly (Traverse | null)[];
}

export interface ShipPassInput {
  readonly ships: readonly ShipSpec[];
  /** Her hull on the target list, so the pass can read the belts it must spend. */
  readonly hulls: readonly EncounterTarget[];
  /** Her battery, already repositioned for this tick. */
  readonly batteries: readonly FlakBattery[];
  readonly quarry: readonly DefensiveQuarry[];
  readonly rng: RngBank;
  readonly tunables: Tunables;
  readonly tick: number;
  readonly dt: number;
}

export interface ShipFire {
  readonly rounds: readonly RoundRecord[];
  readonly belts: ReadonlyMap<string, DefensiveBelt>;
  /** Purely an output for the renderer, exactly as a `FlakBurst` is — never hashed, never carried. */
  readonly aims: readonly ShipAim[];
}

const NO_FIRE: ShipFire = { rounds: [], belts: new Map(), aims: [] };

/** What one mounting is asked: which quarry it may look at, and how far. */
interface AimQuestion {
  readonly quarry: readonly DefensiveQuarry[];
  readonly rangeM: number;
}

/**
 * What each mounting may look at.
 *
 * A **flak** mount is asked only about the aeroplane its own battery slot is
 * laid on. Reading the slot is a report rather than a second opinion: working
 * out from geometry which aeroplane a gun *ought* to be on would be a duplicate
 * acquisition rule that could disagree with the one that fires. A slot with
 * nothing on it gets an empty list, so its gun rests.
 *
 * A **Maxim** is asked the same question `defensiveFire` asked it — the same
 * mount, the same quarry, the same acquisition rule — out to
 * `MG_TRACKING_RANGE_M` rather than to the range she fires at, which is the one
 * deliberate difference: inside the firing envelope the barrel points where the
 * rounds went, and in the margin beyond it the barrel is following an aeroplane
 * the gunner has not opened up on yet.
 *
 * The **4.7 in** is asked about everything hostile inside `MAIN_TRAINING_RANGE_M`.
 */
function aimQuestion(
  index: number,
  battery: FlakBattery | undefined,
  quarry: readonly DefensiveQuarry[],
): AimQuestion | null {
  const slot = SHIP_FLAK_MOUNT_INDICES.indexOf(index);
  if (slot >= 0) {
    const targetId = battery?.slots[slot]?.targetId ?? null;
    const laid = targetId === null ? [] : quarry.filter((entry) => entry.id === targetId);
    return { quarry: laid, rangeM: MAIN_TRAINING_RANGE_M };
  }
  if (SHIP_MG_MOUNT_INDICES.includes(index)) return { quarry, rangeM: MG_TRACKING_RANGE_M };
  if (index === SHIP_MAIN_MOUNT_INDEX) return { quarry, rangeM: MAIN_TRAINING_RANGE_M };
  return null;
}

/** Every mounting on one ship, in `SHIP_MOUNTS` order. */
function mountAims(
  ship: ShipSpec,
  body: CombatBody,
  battery: FlakBattery | undefined,
  quarry: readonly DefensiveQuarry[],
  gunnery: DefensiveGunSpec,
  tunables: Tunables,
): ShipAim {
  // Indexed by the *whole* rig rather than by the two-mount machine-gun spec,
  // so `index` means the same thing here as it does in `SHIP_MOUNTS`.
  const rig: DefensiveGunSpec = { ...gunnery, gun: { ...gunnery.gun, mounts: SHIP_MOUNTS } };
  const mounts = SHIP_MOUNTS.map((_unused, index) => {
    const question = aimQuestion(index, battery, quarry);
    if (!question || question.quarry.length === 0) return null;
    return stationAim(body, index, { ...rig, engagementRangeM: question.rangeM }, question.quarry, ship.side, tunables);
  });
  return { shipId: ship.id, mounts };
}

/**
 * One tick of every ship's Maxims and of every mounting's bearing.
 *
 * Returns `NO_FIRE` — with an empty aim list — the moment there is nothing to
 * do, so a sortie with no ships in it pays one array-length test.
 */
export function shipPass(input: ShipPassInput): ShipFire {
  if (input.ships.length === 0) return NO_FIRE;
  const gunnery = shipGunnery(input.tunables);
  const byId = new Map(input.hulls.map((hull) => [hull.id, hull]));
  const batteryById = new Map(input.batteries.map((battery) => [battery.id, battery]));
  // `shipFightsOn` is the one predicate every consumer asks — this pass, pass 0's
  // battery list and the renderer's beams — so that "she has stopped" cannot
  // come to mean three slightly different things.
  const live = input.ships.filter((ship) => {
    const hull = byId.get(ship.id);
    return hull !== undefined && shipFightsOn(hull);
  });
  if (live.length === 0) return NO_FIRE;

  const poses = new Map(live.map((ship) => [ship.id, targetBody(byId.get(ship.id)!)]));
  const fire = defensiveFire({
    gunners: live.map((ship) => {
      const hull = byId.get(ship.id)!;
      return {
        id: ship.id,
        side: ship.side,
        body: poses.get(ship.id)!,
        gunRoundsRemaining: hull.gunRoundsRemaining,
        gunRoundsFired: hull.gunRoundsFired,
        gunAimBias: hull.gunAimBias,
        gunAimEpoch: hull.gunAimEpoch,
      };
    }),
    spec: gunnery,
    quarry: input.quarry,
    rng: input.rng,
    tunables: input.tunables,
    tick: input.tick,
    dt: input.dt,
  });

  const aims = live.map((ship) =>
    mountAims(ship, poses.get(ship.id)!, batteryById.get(shipBatteryId(ship.id)), input.quarry, gunnery, input.tunables),
  );
  return { rounds: fire.rounds, belts: fire.belts, aims };
}
